import { useMemo, useState } from "react";
import { Skeleton } from "@/components/base/Loading";
import { ASSIGNABLE_ROLES, ROLE_LABELS } from "@/lib/adminModules";
import { formatFullDate } from "@/lib/format";
import type { StaffMember } from "@/pages/admin/staff/hooks/useStaff";

interface StaffListProps {
  members: StaffMember[];
  loading: boolean;
  currentUserId: string | null;
  onSetRole: (userId: string, role: string) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

const roleBadge: Record<string, string> = {
  admin: "bg-primary-100 text-primary-700",
  staff: "bg-accent-100 text-accent-800",
  customer: "bg-background-200 text-foreground-700",
};

export default function StaffList({ members, loading, currentUserId, onSetRole, notify }: StaffListProps) {
  const [keyword, setKeyword] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (member) =>
        (member.full_name ?? "").toLowerCase().includes(term) ||
        (member.email ?? "").toLowerCase().includes(term) ||
        (member.company ?? "").toLowerCase().includes(term),
    );
  }, [members, keyword]);

  const adminCount = members.filter((member) => member.role === "admin").length;
  const staffCount = members.filter((member) => member.role === "staff").length;

  const handleChange = async (member: StaffMember, role: string) => {
    if (role === member.role) return;
    setBusyId(member.id);
    try {
      await onSetRole(member.id, role);
      notify(`已将 ${member.full_name || member.email || "成员"} 调整为「${ROLE_LABELS[role] ?? role}」`, "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "角色调整失败", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">成员与角色</h2>
          <p className="mt-1 text-xs text-foreground-500">
            当前共 {members.length} 位成员，其中超管 {adminCount} 位、客服 {staffCount} 位。可随时把成员调整为「客户 / 客服 / 超管」。
          </p>
        </div>
        <div className="relative w-full lg:max-w-xs">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索姓名、邮箱或公司"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <div className="mt-5 space-y-3">
          {[0, 1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-14 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-user-search-line text-xl" />
          </span>
          <p className="text-sm text-foreground-600">未找到匹配的成员</p>
        </div>
      ) : (
        <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-background-100 text-left text-xs text-foreground-600">
                <th className="px-4 py-3 font-medium">成员</th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">公司</th>
                <th className="hidden px-4 py-3 font-medium lg:table-cell">注册时间</th>
                <th className="px-4 py-3 font-medium">当前角色</th>
                <th className="px-4 py-3 font-medium">调整为</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((member) => {
                const name = member.full_name || member.email?.split("@")[0] || "未命名";
                const isSelf = member.id === currentUserId;
                const isBusy = busyId === member.id;
                return (
                  <tr key={member.id} className="border-t border-background-200">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-background-50">
                          {name.slice(0, 1).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 font-medium text-foreground-950">
                            <span className="truncate">{name}</span>
                            {isSelf && (
                              <span className="rounded-full bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
                                我
                              </span>
                            )}
                          </p>
                          <p className="truncate text-xs text-foreground-500">{member.email || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-foreground-700 md:table-cell">{member.company || "—"}</td>
                    <td className="hidden px-4 py-3 text-xs text-foreground-500 lg:table-cell">
                      {formatFullDate(member.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                          roleBadge[member.role] ?? roleBadge.customer
                        }`}
                      >
                        {ROLE_LABELS[member.role] ?? member.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={member.role}
                        disabled={isBusy}
                        onChange={(event) => {
                          void handleChange(member, event.target.value);
                        }}
                        className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {ASSIGNABLE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {ROLE_LABELS[role] ?? role}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}